/**
 * Offer copy made safe to render as HTML.
 *
 * The copy carries a little formatting on purpose — the service strikes a
 * bundle's old price through with `<s>`, the app's own strings bold with `<b>`
 * — but it also interpolates merchant-controlled text such as item names, so
 * rendering it raw lets a menu item named `<img onerror=…>` run on the guest's
 * page.
 *
 * Escape everything, then restore only bare formatting tags. A tag with any
 * attribute is never restored, so nothing that can carry a script or a URL
 * gets back in. Entities already in the copy are kept, not double-escaped.
 */

const FORMATTING_TAGS = ["b", "strong", "i", "em", "u", "s", "br"];

const ESCAPES: Record<string, string> = {
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

const EXISTING_ENTITY = /^&(#\d+|#x[\da-f]+|[a-z][a-z\d]*);/i;

const ESCAPED_FORMATTING_TAG = new RegExp(
  `&lt;(/?)(${FORMATTING_TAGS.join("|")})\\s*(/?)&gt;`,
  "gi"
);

function escapeHtml(text: string): string {
  let out = "";

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (char === "&") {
      out += EXISTING_ENTITY.test(text.slice(i)) ? "&" : "&amp;";
    } else {
      out += ESCAPES[char] ?? char;
    }
  }

  return out;
}

export function sanitizeOfferHtml(html: string): string {
  return escapeHtml(html).replace(
    ESCAPED_FORMATTING_TAG,
    (_match, closing: string, tag: string, selfClosing: string) =>
      `<${closing}${tag.toLowerCase()}${selfClosing}>`
  );
}
